import { useMutation } from "@tanstack/react-query";
import { Sparkles, Plus, Trash2, RefreshCw } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig, api } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface Props {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["reaction_roles"]) => void;
}

const BUTTON_STYLES = [
  { id: "primary", label: "Blue (primary)" },
  { id: "secondary", label: "Gray (secondary)" },
  { id: "success", label: "Green (success)" },
  { id: "danger", label: "Red (danger)" },
];

function roleColorHex(color: number): string {
  if (color == null || color === 0) return "#99aab5";
  return `#${(color >>> 0).toString(16).padStart(6, "0")}`;
}

function displayRoleName(name: string): string {
  return (name || "").replace(/^@+/, "").trim() || "—";
}

function rolesForSelect(list: DiscordRole[]): DiscordRole[] {
  const seen = new Set<string>();
  return [...list]
    .filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    })
    .sort((a, b) => b.position - a.position);
}

export function ReactionRolesTab({
  guildId,
  config,
  channels,
  roles,
  onChange,
}: Props) {
  const cfg = config.reaction_roles;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  const rolesSorted = rolesForSelect(roles);

  const repostMut = useMutation({
    mutationFn: (panelId: string) =>
      api.repostPanel(guildId, { section: "rr", panel_id: panelId }),
    onSuccess: () =>
      toast.success("Panel message refreshed (same message, not a new post)."),
    onError: (e: Error) => toast.error(e.message),
  });

  const addPanel = () => {
    const pid = `rr_${Date.now()}`;
    update("panels", [
      ...cfg.panels,
      {
        id: pid,
        channel_id: "",
        title: "Pick roles",
        description: "Click a button to get a role.",
        color: "#5865F2",
        mode: "toggle",
        display_style: "buttons",
        buttons: [],
      },
    ]);
  };

  const removePanel = (pid: string) =>
    update(
      "panels",
      cfg.panels.filter((p) => p.id !== pid),
    );

  const updatePanel = (
    pid: string,
    patch: Partial<GuildConfig["reaction_roles"]["panels"][0]>,
  ) =>
    update(
      "panels",
      cfg.panels.map((p) => (p.id === pid ? { ...p, ...patch } : p)),
    );

  return (
    <Section
        icon={Sparkles}
        title="Reaction Roles"
        description="Button panels to assign roles."
        enabled={cfg.enabled}
        onToggle={(v) => update("enabled", v)}
        actions={
          <Button size="sm" onClick={addPanel}>
            <Plus className="h-4 w-4" />
            New panel
          </Button>
        }
      >
        <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
          <p className="font-medium text-foreground">How it works</p>
          <p>
            Build one or more <strong>role panels</strong>; pick channel, interaction style (buttons, reactions, or
            Components v2 sections), mode (toggle vs single role), then add emoji buttons linked to roles.{" "}
            <strong>No member slash commands</strong>—users interact on the posted message. Use <strong>Repost</strong>{" "}
            after saving so Discord shows the latest panel.
          </p>
        </div>

        {cfg.panels.length === 0 && (
          <div className="text-center py-10 border border-dashed border-border rounded-lg text-sm text-muted-foreground">
            No panels yet. Create the first one.
          </div>
        )}

        <div className="space-y-4">
          {cfg.panels.map((panel) => (
            <Card key={panel.id} className="p-4 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold">{panel.title || "(no title)"}</h4>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => repostMut.mutate(panel.id)}
                    disabled={repostMut.isPending}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Repost
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removePanel(panel.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Channel">
                  <ChannelPicker
                    channels={channels}
                    value={panel.channel_id}
                    onChange={(v) =>
                      updatePanel(panel.id, {
                        channel_id: v === "__none__" ? "" : v,
                      })
                    }
                  />
                </Field>
                <Field label="Interaction">
                  <Select
                    value={panel.display_style ?? "buttons"}
                    onValueChange={(v) =>
                      updatePanel(panel.id, {
                        display_style: v as
                          | "buttons"
                          | "reactions"
                          | "sections",
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buttons">Buttons</SelectItem>
                      <SelectItem value="reactions">Reactions</SelectItem>
                      <SelectItem value="sections">Sections (v2)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Mode">
                  <Select
                    value={panel.mode}
                    onValueChange={(v) =>
                      updatePanel(panel.id, { mode: v as "toggle" | "unique" })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="toggle">
                        Toggle (add/remove)
                      </SelectItem>
                      <SelectItem value="unique">
                        Unique (only one at a time)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              {(panel.display_style ?? "buttons") === "reactions" && (
                <p className="text-xs text-amber-400/95">
                  Each role row needs a distinct emoji — the bot mirrors those emojis as reactions on the panel message.
                </p>
              )}

              <Field label="Title">
                <Input
                  value={panel.title}
                  onChange={(e) =>
                    updatePanel(panel.id, { title: e.target.value })
                  }
                />
              </Field>

              <Field label="Description">
                <Textarea
                  value={panel.description}
                  rows={2}
                  onChange={(e) =>
                    updatePanel(panel.id, { description: e.target.value })
                  }
                />
              </Field>

              <Field label="Embed color">
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={panel.color}
                    onChange={(e) =>
                      updatePanel(panel.id, { color: e.target.value })
                    }
                    className="h-10 w-14 rounded border border-border bg-transparent cursor-pointer"
                  />
                  <Input
                    value={panel.color}
                    onChange={(e) =>
                      updatePanel(panel.id, { color: e.target.value })
                    }
                    className="font-mono w-32"
                  />
                </div>
              </Field>

              {/* Buttons */}
              <div className="pt-3 border-t border-border space-y-2">
                <div className="flex items-center justify-between">
                    <h5 className="text-sm font-semibold">
                    Buttons ({panel.buttons.length})
                  </h5>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      updatePanel(panel.id, {
                        buttons: [
                          ...panel.buttons,
                          {
                            idx: `b_${Date.now()}`,
                            label: "",
                            emoji: "",
                            role_id: "",
                            style: "primary",
                          },
                        ],
                      })
                    }
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add button
                  </Button>
                </div>

                {panel.buttons.map((btn, bi) => (
                  <div
                    key={btn.idx}
                    className="grid gap-2 items-center [grid-template-columns:minmax(0,4rem)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,7.5rem)_2.5rem] max-md:grid-cols-1"
                  >
                    <Input
                      className="min-w-0 text-center"
                      maxLength={80}
                      placeholder="🔔"
                      value={btn.emoji}
                      onChange={(e) => {
                        const bs = [...panel.buttons];
                        bs[bi] = { ...bs[bi], emoji: e.target.value };
                        updatePanel(panel.id, { buttons: bs });
                      }}
                    />
                    <Input
                      className="min-w-0"
                      maxLength={80}
                      placeholder="Short label (e.g. Ping) — leave empty for emoji only"
                      value={btn.label}
                      onChange={(e) => {
                        const bs = [...panel.buttons];
                        bs[bi] = { ...bs[bi], label: e.target.value };
                        updatePanel(panel.id, { buttons: bs });
                      }}
                    />
                    <Select
                      value={
                        btn.role_id ? String(btn.role_id) : "__none__"
                      }
                      onValueChange={(v) => {
                        const bs = [...panel.buttons];
                        bs[bi] = {
                          ...bs[bi],
                          role_id: v === "__none__" ? "" : v,
                        };
                        updatePanel(panel.id, { buttons: bs });
                      }}
                    >
                      <SelectTrigger className="min-w-0">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64" position="item-aligned">
                        <SelectItem value="__none__" textValue="No role">
                          (no role)
                        </SelectItem>
                        {rolesSorted.map((r) => {
                          const title = displayRoleName(r.name);
                          return (
                            <SelectItem
                              key={r.id}
                              value={String(r.id)}
                              textValue={title}
                            >
                              <span className="flex min-w-0 max-w-full items-center gap-2 text-left">
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-full border border-border/60"
                                  style={{ background: roleColorHex(r.color) }}
                                />
                                <span className="min-w-0 flex-1 truncate" title={title}>
                                  {title}
                                </span>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Select
                      value={btn.style}
                      onValueChange={(v) => {
                        const bs = [...panel.buttons];
                        bs[bi] = { ...bs[bi], style: v as typeof btn.style };
                        updatePanel(panel.id, { buttons: bs });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUTTON_STYLES.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() =>
                        updatePanel(panel.id, {
                          buttons: panel.buttons.filter((_, i) => i !== bi),
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
    </Section>
  );
}
